import React, {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

/*
 * The 360 charts' one hover system.
 *
 *   const hv = useChartHover({ keys });     keys: the marks in reading order (keyboard steps)
 *   <div {...hv.rootProps}>                  focusable; ←/→ (↑/↓) step, Home/End, Esc hides
 *     <rect {...hv.bind(key)} />             a hit area: pointer hover, touch tap
 *   </div>
 *   <ChartTooltip hover={hv} title=… rows=[…] total=… />
 *
 * `hv.active` is the key under the pointer (or stepped to), `hv.x/hv.y` where to put the card
 * (client px). Touch: a tap shows, a tap anywhere else hides. The tooltip is portalled to
 * <body>, so no card or scroll box can clip it, and flips at the chart's and the viewport's edges.
 */

const STEP_NEXT = new Set(['ArrowRight', 'ArrowDown']);
const STEP_PREV = new Set(['ArrowLeft', 'ArrowUp']);

const escapeKey = (k) => String(k).replace(/["\\]/g, '\\$&');

export function useChartHover({ keys = [], onActivate } = {}) {
  const rootRef = useRef(null);
  const [st, setSt] = useState({ key: null, x: 0, y: 0, via: null });
  const keysRef = useRef(keys);
  keysRef.current = keys;
  const stRef = useRef(st);
  stRef.current = st;

  const show = useCallback((key, x, y, via) => {
    setSt((cur) => (cur.key === key && cur.x === x && cur.y === y && cur.via === via ? cur : { key, x, y, via }));
  }, []);
  const hide = useCallback(() => setSt((cur) => (cur.key == null ? cur : { key: null, x: 0, y: 0, via: null })), []);

  /** Where a mark is on screen (keyboard stepping has no pointer): the top-middle of its hit area. */
  const anchorOf = useCallback((key) => {
    const el = rootRef.current && rootRef.current.querySelector(`[data-hk="${escapeKey(key)}"]`);
    if (!el || !el.getBoundingClientRect) return { x: 0, y: 0 };
    const r = el.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + Math.min(r.height / 2, 14) };
  }, []);

  // A tap shows the card; a tap anywhere outside the chart hides it.
  useEffect(() => {
    if (st.key == null || st.via !== 'touch') return undefined;
    const off = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) hide(); };
    document.addEventListener('pointerdown', off);
    return () => document.removeEventListener('pointerdown', off);
  }, [st.key, st.via, hide]);

  const bind = useCallback((key) => ({
    'data-hk': key,
    onPointerEnter: (e) => { if (e.pointerType !== 'touch') show(key, e.clientX, e.clientY, 'pointer'); },
    onPointerMove: (e) => { if (e.pointerType !== 'touch') show(key, e.clientX, e.clientY, 'pointer'); },
    onPointerDown: (e) => { if (e.pointerType === 'touch') show(key, e.clientX, e.clientY, 'touch'); },
  }), [show]);

  const step = useCallback((dir, edge = false) => {
    const list = keysRef.current;
    if (!list.length) return;
    const i = list.indexOf(stRef.current.key);
    let n;
    if (edge) n = dir > 0 ? list.length - 1 : 0;
    else if (i < 0) n = dir > 0 ? 0 : list.length - 1;
    else n = Math.min(list.length - 1, Math.max(0, i + dir));
    const k = list[n];
    const a = anchorOf(k);
    show(k, a.x, a.y, 'key');
  }, [anchorOf, show]);

  const rootProps = {
    ref: rootRef,
    tabIndex: 0,
    'data-c360-hover': 'true',
    onKeyDown: (e) => {
      if (STEP_NEXT.has(e.key)) { e.preventDefault(); step(1); } else if (STEP_PREV.has(e.key)) { e.preventDefault(); step(-1); } else if (e.key === 'Home') { e.preventDefault(); step(-1, true); } else if (e.key === 'End') { e.preventDefault(); step(1, true); } else if (e.key === 'Escape') { hide(); } else if (e.key === 'Enter' && onActivate && stRef.current.key != null) { onActivate(stRef.current.key); }
    },
    onPointerLeave: (e) => { if (e.pointerType !== 'touch') hide(); },
    onBlur: (e) => {
      if (stRef.current.via === 'key' && !(rootRef.current && rootRef.current.contains(e.relatedTarget))) hide();
    },
  };

  return {
    active: st.key, x: st.x, y: st.y, via: st.via, show, hide, bind, step, rootProps, rootRef,
    /** Opacity for a mark: the hovered one full, the others dimmed while anything is hovered. */
    dim: (key, base = 1) => (st.key == null || st.key === key ? base : base * 0.45),
  };
}

/** The 360 surface tokens live on the page root, not on <body>: carry them onto the portal. */
const TOKENS = ['--s360-card', '--s360-inset', '--border-color', '--text-primary', '--text-secondary', '--shadow-color'];
/**
 * `tokens`: the custom properties to carry, either a list of names (copied as they are) or a map
 * { tooltipVar: sourceVar } for a surface with its own token names (Security Atlas passes its
 * --sa-* tokens this way). The 360 charts pass nothing and get TOKENS.
 */
const tokensFrom = (el, tokens = TOKENS) => {
  if (!el || typeof window === 'undefined' || !window.getComputedStyle) return {};
  const cs = window.getComputedStyle(el);
  const out = {};
  const pairs = Array.isArray(tokens) ? tokens.map((k) => [k, k]) : Object.entries(tokens || {});
  pairs.forEach(([to, from]) => { const v = cs.getPropertyValue(from); if (v && v.trim()) out[to] = v.trim(); });
  return out;
};

const GAP = 14;

/**
 * The hover card. `rows`: [{ key, color, kind ('sq'|'line'|'dash'), label, value, share, strong }];
 * `total`: { label, value }; `title` / `subtitle` above, `note` below. Values arrive formatted.
 * `tokens`: which theme variables to carry onto the portal (see tokensFrom); default the 360 set.
 * `className`: an extra class on the card, for a surface that needs to adjust it.
 */
export function ChartTooltip({
  hover, title, subtitle, rows = [], total, note, id, tokens, className,
}) {
  const { t } = useTranslation('agent360');
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: -9999, top: -9999 });
  const open = hover && hover.active != null;
  const vars = open ? tokensFrom(hover.rootRef.current, tokens) : {};

  useLayoutEffect(() => {
    if (!open || !ref.current) return;
    const box = ref.current.getBoundingClientRect();
    const vw = window.innerWidth || 1024;
    const vh = window.innerHeight || 768;
    const chart = hover.rootRef.current && hover.rootRef.current.getBoundingClientRect();
    const w = box.width || 160;
    const h = box.height || 60;
    // Right of the pointer, unless that runs past the viewport or well past the chart's right edge.
    let left = hover.x + GAP;
    const rightLimit = Math.min(vw - 8, chart && chart.width ? chart.right + w / 2 : vw - 8);
    if (left + w > rightLimit) left = hover.x - GAP - w;
    left = Math.max(8, Math.min(left, vw - w - 8));
    // Above the pointer when there is room, else below.
    let top = hover.y - h - GAP;
    if (top < 8) top = hover.y + GAP;
    top = Math.max(8, Math.min(top, vh - h - 8));
    setPos((cur) => (cur.left === left && cur.top === top ? cur : { left, top }));
  }, [open, hover.rootRef, hover.x, hover.y, hover.active]);

  if (!open || typeof document === 'undefined') return null;
  return createPortal(
    <div ref={ref} className={className ? `c360-tip ${className}` : 'c360-tip'} role="tooltip" id={id} style={{ ...vars, left: pos.left, top: pos.top }} data-testid="chart-tooltip">
      {title && <div className="c360-tip-title">{title}</div>}
      {subtitle && <div className="c360-tip-sub">{subtitle}</div>}
      {rows.length > 0 && (
        <table className="c360-tip-rows">
          <tbody>
            {rows.map((r) => (
              <tr key={r.key ?? r.label} className={r.strong ? 'is-strong' : undefined}>
                <td className="c360-tip-sw">
                  {r.color && <span className={`c360-swatch is-${r.kind || 'sq'}`} style={{ '--swatch': r.color }} />}
                </td>
                <td className="c360-tip-label">{r.label}</td>
                <td className="c360-tip-value">{r.value}</td>
                {rows.some((x) => x.share != null) && <td className="c360-tip-share">{r.share ?? ''}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {total && (
        <div className="c360-tip-total">
          <span>{total.label || t('agent360:charts.total')}</span>
          <b>{total.value}</b>
        </div>
      )}
      {note && <div className="c360-tip-note">{note}</div>}
    </div>,
    document.body,
  );
}

/**
 * The size a filling chart draws at: the measured box (a flex:1 plot container), never below
 * `minHeight`. Without `fill` it is the measured width and the fixed `height`.
 */
export function usePlotSize({ fill = false, height = 200, minHeight = 220, initialWidth = 640 } = {}) {
  const [size, setSize] = useState({ w: initialWidth, h: fill ? Math.max(minHeight, height) : height });
  const cleanup = useRef(null);
  const ref = useCallback((el) => {
    if (cleanup.current) { cleanup.current(); cleanup.current = null; }
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      const w = Math.round(r.width);
      const h = Math.round(r.height);
      setSize((cur) => {
        const next = { w: w > 0 ? w : cur.w, h: fill ? Math.max(minHeight, h > 0 ? h : cur.h) : height };
        return next.w === cur.w && next.h === cur.h ? cur : next;
      });
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      cleanup.current = () => window.removeEventListener('resize', measure);
      return;
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    cleanup.current = () => ro.disconnect();
  }, [fill, height, minHeight]);
  return [ref, size];
}

/**
 * A plot box: with `fill`, a flex:1 container (min `minHeight`) whose measured size the chart
 * draws at, the SVG laid over it so its own size never feeds back into the measurement.
 */
export function PlotBox({
  fill, minHeight = 220, height, plotRef, children, className = '',
}) {
  return (
    <div ref={plotRef} className={`c360-plot${fill ? ' is-fill' : ''}${className ? ` ${className}` : ''}`}
      style={fill ? { minHeight } : { height }}>
      {children}
    </div>
  );
}
